<template>
  <b-row class="border-bottom py-3" v-if="reportingEnabled">
    <b-col cols="12" lg="3" class="text-muted">Reports:</b-col>
    <b-col cols="12" lg="9">
      <dl>
        <template v-for="report in submissions">
          <dt :key="`dt-${report.id}`">
            <a
              :href="`#report-${report.id}`"
              @click="toggleReport(report, $event)"
              class="diary-report-link"
            >
              <b-icon
                icon="chevron-right"
                class="diary-chevron mr-1"
                :class="{ expanded: report.detailsOpen }"
              />
              {{ report.answers['report-date'] | moment('L') }}
              <span
                class="font-weight-normal text-muted"
                v-if="report.answers['custom-title']"
              >
                - {{ report.answers['custom-title'] }}
              </span>
              <small class="text-muted" v-if="report.contact">
                by
                <span class="text-body">
                  {{ report.contact.firstName }} {{ report.contact.lastName }}
                </span>
                ({{ report.contact.account.name }})
              </small>
            </a>
          </dt>
          <dd
            :key="`dd-${report.id}`"
            :id="`report-${report.id}`"
            class="border-bottom"
          >
            <b-spinner v-if="report.loading" small></b-spinner>
            <form-answers
              v-if="report.detailsOpen && report.details"
              :answers="report.details"
              :complete-order="order"
              class="mt-2"
            />
          </dd>
        </template>
      </dl>
      <template v-if="canSubmitReport">
        <order-intake-forms
          v-if="formVisible"
          elementId="new-report-iframe"
          :form="form_id"
          :locale="locale"
          :opportunitySfid="this.order.opportunity.sfid"
          :orderApiId="this.order.api_id"
          :accessToken="this.access_token"
          @submit="reportSubmited"
        />
        <b-button
          v-if="!formVisible"
          variant="outline-success"
          size="sm"
          class="diary-add-btn"
          @click="formVisible = true"
        >
          {{ $t('diary.add') }}
        </b-button>
        <b-button
          v-else
          variant="outline-secondary"
          size="sm"
          @click="formVisible = false"
        >
          {{ $t('diary.cancel') }}
        </b-button>
      </template>
    </b-col>
  </b-row>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import OrderIntakeForms from '@gid/vue-common/components/OrderIntakeForms';
import FormAnswers from '@gid/vue-common/components/inputs/FormAnswers.vue';
import { JobStatusEnum } from '@gid/models';

export default {
  components: {
    OrderIntakeForms,
    FormAnswers,
  },
  props: {
    order: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      submissions: [],
      formVisible: false,
    };
  },
  computed: {
    ...mapGetters(['locale', 'access_token']),
    form_id() {
      return this.order.opportunity.multiday_documentation_form_id;
    },
    reportingEnabled() {
      return this.form_id;
    },
    canSubmitReport() {
      return [
        JobStatusEnum.ACCEPTED,
        JobStatusEnum.APPOINTMENT_CONFIRMED,
        JobStatusEnum.ON_HOLD,
        JobStatusEnum.STARTED,
        JobStatusEnum.FINISHED,
        JobStatusEnum.APPROVAL_PENDING,
      ].includes(this.order.job.status);
    },
  },
  methods: {
    reportSubmited() {
      this.formVisible = false;
      this.loadReports();
    },
    async loadReports() {
      const query = RequestQueryBuilder.create()
        .search({
          'job.id': { [CondOperator.EQUALS]: this.order.job.sfid },
          'form.id': { [CondOperator.EQUALS]: this.form_id },
        })
        .query();
      this.submissions = await axios
        .get(`/data-api/entity/submission/?${query}`)
        .then(({ data }) =>
          data
            .map((s) => ({
              ...s,
              answers: s.answers.reduce((all, a) => {
                all[a.question.name] = a.answer;
                return all;
              }, {}),
            }))
            .sort(
              (a, b) =>
                new Date(b.answers['report-date']) -
                new Date(a.answers['report-date']),
            ),
        );
    },
    toggleReport(report, event) {
      event.preventDefault();
      this.$set(report, 'detailsOpen', !report.detailsOpen);
      if (report.details === undefined) {
        this.$set(report, 'details', null);
        this.$set(report, 'loading', true);
        axios
          .get(
            `/order-intake-forms-api/form/answers/${this.order.job.api_id}?submission-id=${report.id}`,
          )
          .then(({ data }) => (report.details = data))
          .finally(() => (report.loading = false));
      }
    },
  },
  created() {
    if (this.reportingEnabled) {
      this.loadReports();
    }
  },
};
</script>

<style scoped>
.diary-report-link {
  cursor: pointer;
}

.diary-chevron {
  transition: transform 0.2s ease-in-out;
}

.diary-chevron.expanded {
  transform: rotate(90deg);
}

.diary-add-btn {
  color: #00a98f;
  border-color: #00a98f;
}
.diary-add-btn:hover,
.diary-add-btn:focus,
.diary-add-btn:active {
  color: #fff;
  background-color: #00a98f;
  border-color: #00a98f;
}
</style>
